import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { Typography } from 'antd'
import { filter, isEmpty, isNil, keys, map, size, some } from 'lodash'
import PropTypes from 'prop-types'

import { useOutsideClick } from '../../../core/hooks'
import { areSameValue } from '../../../core/utils'

import { MetadataTag, TreeSelect } from '../../../common/components/sections'
import { StyledEditabledSelectContainer } from '../../../common/components/styled'
import { HOTKEYS_ENABLED_TAGS } from '../../../common/constants'
import { treeOptionPropType } from '../../../common/prop-types'

import {
  DISH_DRAWER_TYPES,
  DISH_FIELDS_CONFIG,
  DISHES_AVAILABLE_HOTKEYS,
} from '../../constants'
import { useDishEditableCellHotkey, useUpdateDishMutation } from '../../hooks'
import {
  dishPropType,
  ingredientsGroupedByFieldNamePropType,
} from '../../prop-types'
import { transformDishToUpdateArgs } from '../../transformers'
import { StyledIngredientsCellWrapper } from '../styled'

import DishDrawerButton from './dish-drawer-button'

const INGREDIENT_LABELS = {
  mainIngredients: `${DISH_FIELDS_CONFIG.MAIN_INGREDIENTS.label}:`,
  additionalIngredients: `${DISH_FIELDS_CONFIG.ADDITIONAL_INGREDIENTS.label}:`,
  choiceIngredients: `${DISH_FIELDS_CONFIG.CHOICE_INGREDIENTS.label}:`,
}

const DishEditableIngredientsCell = ({
  ingredientsGroupedByFieldName,
  defaultComponent,
  isDisabled,
  dish,
  fieldName,
  options,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [ingredientChangesToSave, setIngredientChangesToSave] = useState({})
  const [isSelectInFocus, setIsSelectInFocus] = useState(false)
  const [selectedInputIndex, setSelectedInputIndex] = useState(0)
  const inputRef = useRef([])
  const [updateDish] = useUpdateDishMutation(dish?.id)
  const { isSelectInHotkeyEditMode, setHotkeyEditModeEnabled, isFocusOnCell } =
    useDishEditableCellHotkey(dish.id, fieldName)

  const handleSave = useCallback(() => {
    if (isSelectInFocus || !isEditing) {
      return
    }

    setIsEditing(false)
    setHotkeyEditModeEnabled(false)
    if (isEmpty(ingredientChangesToSave)) {
      return
    }

    updateDish(transformDishToUpdateArgs(ingredientChangesToSave))

    setIngredientChangesToSave({})
  }, [
    isSelectInFocus,
    isEditing,
    setHotkeyEditModeEnabled,
    ingredientChangesToSave,
    updateDish,
  ])

  const dishIngredientsSelectRef = useRef(null)
  useOutsideClick(dishIngredientsSelectRef, handleSave)

  const handleChange = useCallback(
    (field, newValues) => {
      const existingIngredients = filter(
        ingredientsGroupedByFieldName[field],
        ({ ingredient }) =>
          some(newValues, newId => areSameValue(newId, ingredient.id)),
      )
      const newIngredientIds = filter(newValues, newId => {
        const isNew = !some(
          ingredientsGroupedByFieldName[field],
          ({ ingredient }) => areSameValue(newId, ingredient.id),
        )
        const otherFields = filter(
          keys(ingredientsGroupedByFieldName),
          fieldName => fieldName !== field,
        )
        const isAlreadyUsed = some(
          otherFields,
          fieldName =>
            some(ingredientsGroupedByFieldName[fieldName], ({ ingredient }) =>
              areSameValue(newId, ingredient.id),
            ) ||
            some(ingredientChangesToSave[fieldName], ingredientId =>
              areSameValue(newId, ingredientId),
            ),
        )

        return isNew && !isAlreadyUsed
      })

      setIngredientChangesToSave(prevIngredientChangesToSave => ({
        ...prevIngredientChangesToSave,
        [field]: [
          ...map(existingIngredients, ({ ingredient }) => ingredient.id),
          ...newIngredientIds,
        ],
      }))
    },
    [ingredientsGroupedByFieldName, ingredientChangesToSave],
  )

  const handleClick = useCallback(
    e => {
      e.stopPropagation()
      if (!isDisabled) {
        setIsEditing(true)
      }
    },
    [isDisabled],
  )

  const handleBlur = useCallback(() => setIsSelectInFocus(false), [])
  const handleFocus = useCallback(() => setIsSelectInFocus(true), [])

  useEffect(() => {
    if (isSelectInHotkeyEditMode) {
      setIsEditing(isSelectInHotkeyEditMode)
    }
  }, [isSelectInHotkeyEditMode])

  useEffect(() => {
    if (isEditing && !isEmpty(inputRef.current)) {
      inputRef.current[0]?.focus()
      setSelectedInputIndex(0)
    }
  }, [isEditing])

  useHotkeys(
    DISHES_AVAILABLE_HOTKEYS.ESC.hotkey,
    () => {
      handleBlur()
      handleSave()
    },
    {
      enabled: isEditing,
      enableOnTags: HOTKEYS_ENABLED_TAGS,
    },
    [isEditing, handleSave, handleBlur],
  )

  // key to cycle between ingredient inputs
  useHotkeys(
    DISHES_AVAILABLE_HOTKEYS.TAB.hotkey,
    event => {
      const newSelectIndex =
        selectedInputIndex < size(inputRef.current) - 1
          ? selectedInputIndex + 1
          : 0
      if (!isNil(inputRef.current[newSelectIndex])) {
        inputRef.current[newSelectIndex].focus()
        setSelectedInputIndex(newSelectIndex)
        event.preventDefault()
      }
    },
    {
      enabled: isFocusOnCell && isEditing,
      enableOnTags: HOTKEYS_ENABLED_TAGS,
    },
    [selectedInputIndex, isFocusOnCell, isEditing],
  )

  return (
    <StyledIngredientsCellWrapper ref={dishIngredientsSelectRef}>
      <StyledEditabledSelectContainer
        onClick={handleClick}
        role="presentation"
        isDisabled={isDisabled}
      >
        {(!isEditing || isDisabled) && defaultComponent}
        {isEditing &&
          !isDisabled &&
          map(keys(ingredientsGroupedByFieldName), (field, index) => (
            <div key={field}>
              <Typography.Text>{INGREDIENT_LABELS[field]}</Typography.Text>
              <br />
              <TreeSelect
                ref={el => {
                  inputRef.current[index] = el
                }}
                tagRender={MetadataTag}
                multiple
                showSearch
                allowClear
                disabled={isDisabled}
                defaultValue={map(
                  ingredientsGroupedByFieldName[field],
                  ({ ingredient }) => ingredient.id,
                )}
                getPopupContainer={() => dishIngredientsSelectRef.current}
                treeData={options}
                dropdownStyle={{ maxHeight: 400, minWidth: 250 }}
                dropdownClassName="dark-dropdown-highlights"
                onClick={handleClick}
                onChange={newValue => handleChange(field, newValue)}
                onBlur={handleBlur}
                onFocus={handleFocus}
                maxTagCount={null}
              />
            </div>
          ))}
      </StyledEditabledSelectContainer>
      {!isEmpty(dish[fieldName]) && (
        <DishDrawerButton
          dishId={dish.id}
          drawerType={
            fieldName === DISH_FIELDS_CONFIG.MAIN_INGREDIENTS.value
              ? DISH_DRAWER_TYPES.MAIN_INGREDIENTS
              : DISH_DRAWER_TYPES.CHOICE_INGREDIENTS
          }
        />
      )}
    </StyledIngredientsCellWrapper>
  )
}

DishEditableIngredientsCell.propTypes = {
  defaultComponent: PropTypes.node.isRequired,
  ingredientsGroupedByFieldName: ingredientsGroupedByFieldNamePropType,
  isDisabled: PropTypes.bool,
  dish: dishPropType.isRequired,
  fieldName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(treeOptionPropType),
}

DishEditableIngredientsCell.defaultProps = {
  isDisabled: false,
  ingredientsGroupedByFieldName: {},
  options: [],
}

export default DishEditableIngredientsCell
